import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface DbBrand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  logo_height: string;
  description_en: string | null;
  description_lv: string | null;
  description_ru: string | null;
  origin_country: string | null;
  founded: string | null;
  shopify_vendor: string | null;
  vendor_aliases: string[];
  categories: Record<string, { subcategories: string[] }>;
  is_active: boolean;
  sort_order: number;
  website_url: string | null;
  catalog_url: string | null;
}

let cachedBrands: DbBrand[] | null = null;
let fetchPromise: Promise<void> | null = null;

async function loadBrands(): Promise<DbBrand[]> {
  if (cachedBrands) return cachedBrands;
  if (fetchPromise) { await fetchPromise; return cachedBrands || []; }

  fetchPromise = (async () => {
    try {
      const { data } = await (supabase.from as any)('brands')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });
      cachedBrands = ((data as DbBrand[]) || []).sort((a, b) =>
        a.name.localeCompare(b.name, 'lv', { sensitivity: 'base' })
      );
    } catch {
      cachedBrands = [];
    }
    fetchPromise = null;
  })();
  await fetchPromise;
  return cachedBrands || [];
}

// Pre-load on import
loadBrands();

export function useBrands() {
  const [brands, setBrands] = useState<DbBrand[]>(cachedBrands || []);
  const [isLoading, setIsLoading] = useState(!cachedBrands);

  useEffect(() => {
    loadBrands().then(b => {
      setBrands(b);
      setIsLoading(false);
    });
  }, []);

  // Listen for changes
  useEffect(() => {
    const channel = supabase
      .channel(`brands-updates-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'brands' }, () => {
        cachedBrands = null;
        loadBrands().then(setBrands);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  return { brands, isLoading };
}

// Synchronous getter for already-loaded brands (returns empty if not loaded yet)
export function getCachedBrands(): DbBrand[] {
  return cachedBrands || [];
}

// Find brand by vendor name
export function findBrandByVendor(vendor: string): DbBrand | null {
  if (!cachedBrands) return null;
  const upper = vendor.toUpperCase().trim();

  // 1. Exact match on shopify_vendor
  for (const brand of cachedBrands) {
    if (brand.shopify_vendor && brand.shopify_vendor.toUpperCase().trim() === upper) return brand;
  }
  // 2. Exact match on name
  for (const brand of cachedBrands) {
    if (brand.name.toUpperCase().trim() === upper) return brand;
  }
  // 3. Exact match on aliases
  for (const brand of cachedBrands) {
    if (brand.vendor_aliases?.some(a => a.toUpperCase().trim() === upper)) return brand;
  }
  // No partial/substring matching — it caused false positives
  // (e.g. "HAUS" matching "BADEN HAUS"). If a vendor needs to map to
  // a brand, add it as an explicit alias in the brand record.
  return null;
}

// Find brand by slug
export function findBrandBySlug(slug: string): DbBrand | null {
  if (!cachedBrands) return null;
  const normalized = slug.toLowerCase().replace(/\s+/g, '-').replace(/\+/g, '-');
  
  // Exact match only to avoid false positives
  for (const brand of cachedBrands) {
    if (brand.slug === normalized) return brand;
  }
  return null;
}
